import { Injectable, Injector, inject } from '@angular/core';
import moment, { Moment } from 'moment';
import { EMPTY, Observable, concat, forkJoin, from as from$, of } from 'rxjs';
import {
    catchError,
    map,
    mergeMap,
    reduce,
    shareReplay,
    switchMap,
    toArray,
} from 'rxjs/operators';
import {
    compact as _compact,
    get as _get,
    toString as _toString,
    uniq as _uniq,
    uniqBy as _uniqBy,
} from 'lodash';
import {
    LazadaStoreConfig,
    ShopeeStoreConfig,
    StoreConfig,
    StoreId,
    STORES,
    TiktokStoreConfig,
} from '../../config/store-config';
import {
    SHOPEE_LIST_WINDOW_DAYS,
    splitWindows,
    storeService,
} from '../common/store-scoped';
import { STORE_SYNC_CONCURRENCY } from '../common/throttle-retry';
import { LazadaOpenService } from '../lazada/lazada-open.service';
import { OrderStatus as LazadaOrderStatus } from '../lazada/enums/order-status';
import { ShopeeOpenServiceV2 } from '../shopee/shopee-open-v2.service';
import { OrderStatus as ShopeeOrderStatus } from '../shopee/enums/order-status-v2';
import { TiktokOpenService } from '../tiktok/tiktok-open.service';
import { OrderStatus as TiktokOrderStatus } from '../tiktok/enums/order-status';
import { UtilService } from '../util.service';
import {
    ReturnPackage,
    ShopeeForwardTracking,
    awaitingSellerDecision,
    isShopeeFailedDelivery,
    isShopeeInTransitRefund,
    isTiktokFailedDelivery,
    normalizeLazadaFailedDelivery,
    normalizeLazadaReverseOrder,
    normalizeShopeeFailedDelivery,
    normalizeShopeeReturn,
    normalizeTiktokFailedDelivery,
    normalizeTiktokReturn,
} from './return-package';
import { IndexResponse, packageFromIndex } from './return-index';
import { ReturnsApiService } from './returns-api.service';

// Pulls every return opened in the last N days from all five stores — and
// every failed delivery coming back in that time — one emission per store
// as each finishes, so the page can fill in as the fast stores answer
// instead of waiting for the slowest. A store that fails does not abort the
// others: its error is reported next to their counts, because a table
// silently missing one shop is exactly the case an operator cannot see.

export interface ReturnFetchReport {
    storeId: StoreId;
    title: string;
    rows: number;
    error?: string;
    // The returns loaded but the failed deliveries did not: the store's
    // "ตีกลับ" parcels are missing from the table.
    failedDeliveryError?: string;
    // Shopee only: returns whose detail (the call carrying the logistics
    // status) failed, or parcels coming back on their forward label whose
    // tracking failed. They are still listed, so their stage cannot read
    // 'delivered' — the operator should know.
    detailFailures?: number;
    // Read from the collector's index: its last successful run for this
    // store. Absent for a live fetch.
    indexedAt?: Moment;
}

export interface StoreReturnFetch {
    rows: ReturnPackage[];
    report: ReturnFetchReport;
}

export interface AwaitingDecisionResult {
    rows: ReturnPackage[];
    // Titles of the stores that could not be asked — the count is a floor
    // while this is non-empty.
    failed: string[];
}

// How far back the awaiting-decision badge looks. Shopee gives the seller
// a few days to respond and TikTok auto-approves after that, so anything
// older is no longer waiting on the shop. Exported for the badge's hint,
// which must say the same number.
export const AWAITING_LOOKBACK_DAYS = 30;
// Ten minutes: a request the shop has a week to answer does not need a
// fresher badge, and the refresh button forces a lookup when it matters.
const AWAITING_CACHE_MS = 10 * 60 * 1000;

// For the awaiting-decision lookup, how much earlier than the lookback the
// Lazada ORDER-date window starts (that is the only filter Lazada offers).
// Lazada gives the seller 3 days to decide and refunds automatically after
// that, so a request still waiting was opened this week; 7 days of margin
// covers a return opened right at the end of Lazada's 15-day window on an
// order that took a while to deliver, without reading the 60 days the full
// tracker needs.
const AWAITING_LAZADA_ORDER_MARGIN_DAYS = 7;

// How long after an order is placed a return can still be opened on Lazada.
// Its reverse-order endpoint filters on the ORDER's creation date, so the
// query window starts this much before the lookback. Lazada's own policy is
// 15 days from delivery; 60 covers slow deliveries and disputes.
const LAZADA_ORDER_TO_RETURN_DAYS = 60;

// Shopee detail calls in flight per store. These are single-return reads,
// far lighter than the finance chunks that FINANCE_FETCH_CONCURRENCY was
// tuned for, and they are the long pole of a load.
const SHOPEE_DETAIL_CONCURRENCY = 4;

// Shopee list statuses under which the case is over: the logistics status
// can no longer change what the tracker shows (closed / cancelled either
// way), so the per-return detail call is not worth making.
const SHOPEE_FINAL_STATUSES = ['REFUND_PAID', 'CLOSED', 'CANCELLED'];

// How long after an order is placed it can still come back undelivered.
// The order endpoints filter on creation (Lazada) or the stage that matters
// happens days after it, so the window starts this much earlier and the
// rows are then cut to the lookback by when they turned around. Observed:
// TikTok 4–11 days from order to return, Lazada up to a few weeks.
const LAZADA_ORDER_TO_FAILURE_DAYS = 30;
const TIKTOK_ORDER_TO_FAILURE_DAYS = 15;

// Shopee tracking calls in flight per store: two per failed delivery (the
// label, then its history) on top of the return details.
const SHOPEE_TRACKING_CONCURRENCY = 2;

// How far back the collector's index reaches (its INDEX_DAYS); a longer
// lookback sweeps the platforms live.
const INDEX_DAYS = 90;
// A store whose last successful collector run is older than this is not
// trusted: the collector runs every 15 min in shop hours plus a daily full
// sweep, so this only trips when it is broken.
const INDEX_STALE_HOURS = 26;
// How far back the live top-up over the index reads failed deliveries.
const TOP_UP_DAYS = 2;

// A live row that knows less of the parcel's journey than the index does:
// its label or tracking call failed (tracking only ever grows).
const lessTracked = (live: ReturnPackage, indexed: ReturnPackage): boolean =>
    (!live.trackingNumber && !!indexed.trackingNumber) ||
    live.trackingEvents.length < indexed.trackingEvents.length;

// What a fetch is for. The tracker wants everything; the awaiting-decision
// badge only return requests (a failed delivery is never waiting on the
// shop), without Shopee's per-return detail calls, over a short window.
type FetchMode = 'tracker' | 'awaiting';

// One half of a store's fetch (returns, or failed deliveries).
interface PartialFetch {
    rows: ReturnPackage[];
    detailFailures?: number;
}

@Injectable({
    providedIn: 'root',
})
export class ReturnOrdersService {
    private injector = inject(Injector);
    private utilService = inject(UtilService);
    private returnsApi = inject(ReturnsApiService);
    private awaitingCache: {
        at: number;
        result: Observable<AwaitingDecisionResult>;
    } | null = null;

    // What the returns pages show: the collector's index when it is fresh
    // and reaches back far enough — instant, one call — merged with the
    // last TOP_UP_DAYS of failed deliveries fetched live (a TikTok one is
    // listed only once it is back at the shop, so no schedule has it in
    // time). Otherwise the live sweep, as before the index; its reports
    // carry no `indexedAt`, which is how a page tells the two apart.
    //
    // `progressive` (a first load) emits each store's index rows at once
    // and the store again once its top-up lands — each emission REPLACES
    // that store's rows. Without it (a refresh of a table already on
    // screen) each store emits once, complete, so rows only the top-up
    // finds never blink out in between.
    load(
        lookbackDays: number,
        { progressive = true }: { progressive?: boolean } = {},
    ): Observable<StoreReturnFetch> {
        if (lookbackDays > INDEX_DAYS) {
            return this.fetchEach(lookbackDays);
        }
        const since = moment().startOf('day').subtract(lookbackDays, 'days');
        return this.readIndex(since).pipe(
            catchError((error: unknown) => {
                console.warn(
                    'return index unreadable — sweeping live:',
                    this.utilService.errorToString(error),
                );
                return of(null);
            }),
            switchMap((fetches) =>
                !fetches
                    ? this.fetchEach(lookbackDays)
                    : progressive
                      ? concat(from$(fetches), this.topUp(fetches))
                      : this.topUp(fetches),
            ),
        );
    }

    // The index as one fetch per store, or null when any store's part is
    // stale or does not reach back to `since` — then nothing from it is
    // used, so the page never mixes a fresh store with a stale one.
    private readIndex(since: Moment): Observable<StoreReturnFetch[] | null> {
        return this.returnsApi
            .request<IndexResponse>('index', { since: since.unix() })
            .pipe(
                map(({ packages, state }) => {
                    const fresh = moment().subtract(INDEX_STALE_HOURS, 'hours');
                    const states = new Map(state.map((s) => [s._id, s]));
                    const unusable = _compact(
                        STORES.map((store) => {
                            const s = states.get(store.storeId);
                            const at = s?.lastSuccessAt
                                ? moment(s.lastSuccessAt)
                                : null;
                            if (!at?.isValid()) {
                                return `${store.storeId}: never collected`;
                            }
                            if (!at.isAfter(fresh)) {
                                return `${store.storeId}: last collected ${at.format()}`;
                            }
                            if (!(Number(s?.coversFrom) <= since.unix())) {
                                return `${store.storeId}: covers from ${moment.unix(Number(s?.coversFrom)).format()}`;
                            }
                            return null;
                        }),
                    );
                    if (unusable.length) {
                        console.warn(
                            'return index not usable — sweeping live:',
                            unusable.join('; '),
                        );
                        return null;
                    }
                    return STORES.map((store): StoreReturnFetch => {
                        const s = states.get(store.storeId);
                        const docs = packages.filter(
                            (doc) => doc.storeId === store.storeId,
                        );
                        const rows = _compact(
                            docs.map((doc) => packageFromIndex(store, doc)),
                        ).filter((row) => this.openedSince(row, since));
                        const detailFailures = docs.filter(
                            (doc) => !!doc.fetchError,
                        ).length;
                        const indexedAt = moment(s?.lastSuccessAt);
                        // The last collector run failed a half: the rows
                        // are as of indexedAt, and the next run retries.
                        const lastRun = (error: string) =>
                            `รอบเก็บข้อมูลล่าสุดไม่สำเร็จ ใช้ข้อมูลเมื่อ ${indexedAt.format('HH:mm')} — ${error}`;
                        return {
                            rows,
                            report: {
                                storeId: store.storeId,
                                title: store.title,
                                rows: rows.length,
                                indexedAt,
                                ...(s?.returnsError
                                    ? { error: lastRun(s.returnsError) }
                                    : {}),
                                ...(s?.failedDeliveryError
                                    ? {
                                          failedDeliveryError: lastRun(
                                              s.failedDeliveryError,
                                          ),
                                      }
                                    : {}),
                                ...(detailFailures ? { detailFailures } : {}),
                            },
                        };
                    });
                }),
            );
    }

    // Each store again, its index rows merged with the failed deliveries of
    // the last TOP_UP_DAYS fetched live. Live wins on the same package —
    // unless it came back with less than the index has (a Shopee label or
    // tracking call that failed), since tracking only ever grows. A store
    // whose top-up fails still emits its index rows, saying so: the next
    // collector run brings today's failed deliveries in.
    private topUp(fetches: StoreReturnFetch[]): Observable<StoreReturnFetch> {
        const since = moment().subtract(TOP_UP_DAYS, 'days');
        return from$(fetches).pipe(
            mergeMap((fetch) => {
                const store = STORES.find(
                    (s) => s.storeId === fetch.report.storeId,
                );
                if (!store) {
                    return EMPTY;
                }
                return this.fetchFailedDeliveries(store, since).pipe(
                    map((live): StoreReturnFetch => {
                        const byId = new Map(
                            fetch.rows.map((row) => [row.id, row]),
                        );
                        for (const row of live.rows) {
                            const indexed = byId.get(row.id);
                            if (!indexed || !lessTracked(row, indexed)) {
                                byId.set(row.id, row);
                            }
                        }
                        const rows = [...byId.values()];
                        const detailFailures =
                            (fetch.report.detailFailures ?? 0) +
                            (live.detailFailures ?? 0);
                        return {
                            rows,
                            report: {
                                ...fetch.report,
                                rows: rows.length,
                                ...(detailFailures ? { detailFailures } : {}),
                            },
                        };
                    }),
                    catchError((error: unknown) =>
                        of<StoreReturnFetch>({
                            rows: fetch.rows,
                            report: {
                                ...fetch.report,
                                failedDeliveryError: `${TOP_UP_DAYS} วันล่าสุดดึงสดไม่ได้ (แสดงตามรอบเก็บข้อมูล) — ${this.utilService.errorToString(error)}`,
                            },
                        }),
                    ),
                );
            }, STORE_SYNC_CONCURRENCY),
        );
    }

    // One emission per store, in completion order (not STORES order), then
    // complete. Stores run at STORE_SYNC_CONCURRENCY, not all at once — see
    // throttle-retry.ts for why five in parallel was too many.
    fetchEach(lookbackDays: number): Observable<StoreReturnFetch> {
        const since = moment().startOf('day').subtract(lookbackDays, 'days');
        return from$(STORES).pipe(
            mergeMap(
                (store) => this.fetchStore(store, since, 'tracker'),
                STORE_SYNC_CONCURRENCY,
            ),
        );
    }

    // The return requests whose next move is the shop's — approve or reject
    // — across every store, for the badge on the orders page. Lighter than
    // fetchEach: no Shopee detail calls (the request status is on the list
    // row) and a short window, since the platforms auto-resolve a request
    // the shop ignores within days. Cached briefly so switching order tabs
    // does not refetch three platforms each time; `force` bypasses that.
    fetchAwaitingDecision(force = false): Observable<AwaitingDecisionResult> {
        const now = Date.now();
        if (
            !force &&
            this.awaitingCache &&
            now - this.awaitingCache.at < AWAITING_CACHE_MS
        ) {
            return this.awaitingCache.result;
        }
        const since = moment()
            .startOf('day')
            .subtract(AWAITING_LOOKBACK_DAYS, 'days');
        const shared = from$(STORES).pipe(
            mergeMap(
                (store) => this.fetchStore(store, since, 'awaiting'),
                STORE_SYNC_CONCURRENCY,
            ),
            toArray(),
            map((fetches) => ({
                rows: fetches
                    .flatMap((fetch) => fetch.rows)
                    .filter(awaitingSellerDecision),
                failed: fetches
                    .filter((fetch) => !!fetch.report.error)
                    .map((fetch) => fetch.report.title),
            })),
            shareReplay(1),
        );
        this.awaitingCache = { at: now, result: shared };
        return shared;
    }

    // The store's returns and — for the tracker — its failed deliveries,
    // side by side. A failed-delivery fetch that fails is reported on its
    // own and keeps the returns; a failed returns fetch fails the store.
    private fetchStore(
        store: StoreConfig,
        since: Moment,
        mode: FetchMode,
    ): Observable<StoreReturnFetch> {
        const tracker = mode === 'tracker';
        const returns: Observable<PartialFetch> =
            store.platform === 'lazada'
                ? this.fetchLazada(
                      store,
                      since,
                      tracker
                          ? LAZADA_ORDER_TO_RETURN_DAYS
                          : AWAITING_LAZADA_ORDER_MARGIN_DAYS,
                  )
                : store.platform === 'shopee'
                  ? this.fetchShopee(store, since, tracker)
                  : this.fetchTiktok(store, since);
        const failedDeliveries: Observable<PartialFetch | { error: string }> =
            !tracker
                ? of({ rows: [] })
                : this.fetchFailedDeliveries(store, since).pipe(
                      catchError((error: unknown) =>
                          of({
                              error: this.utilService.errorToString(error),
                          }),
                      ),
                  );
        return forkJoin([returns, failedDeliveries]).pipe(
            map(([fetched, failed]) => {
                const rows =
                    'error' in failed
                        ? fetched.rows
                        : fetched.rows.concat(failed.rows);
                const detailFailures =
                    (fetched.detailFailures ?? 0) +
                    ('error' in failed ? 0 : (failed.detailFailures ?? 0));
                return {
                    rows,
                    report: {
                        storeId: store.storeId,
                        title: store.title,
                        rows: rows.length,
                        ...('error' in failed
                            ? { failedDeliveryError: failed.error }
                            : {}),
                        ...(detailFailures ? { detailFailures } : {}),
                    },
                };
            }),
            catchError((error: unknown) =>
                of({
                    rows: [],
                    report: {
                        storeId: store.storeId,
                        title: store.title,
                        rows: 0,
                        error: this.utilService.errorToString(error),
                    },
                }),
            ),
        );
    }

    // `orderMarginDays` is how much earlier than `since` the ORDER-date
    // window starts, since that is the only filter Lazada offers.
    private fetchLazada(
        store: LazadaStoreConfig,
        since: Moment,
        orderMarginDays: number,
    ): Observable<{ rows: ReturnPackage[] }> {
        const service = storeService(
            this.injector,
            store,
            'lazadaAuthInfo',
            LazadaOpenService,
        );
        return service
            .getReverseOrders(
                since.clone().subtract(orderMarginDays, 'days'),
                moment(),
            )
            .pipe(
                map((rows) => ({
                    rows: rows
                        .map((row) => normalizeLazadaReverseOrder(store, row))
                        .filter((row) => this.openedSince(row, since)),
                })),
            );
    }

    // The list in 14-day windows, one after another, then the detail of each
    // return still open — the list omits the logistics status, which is the
    // only field that says the carrier delivered the package back.
    private fetchShopee(
        store: ShopeeStoreConfig,
        since: Moment,
        withDetails: boolean,
    ): Observable<{ rows: ReturnPackage[]; detailFailures: number }> {
        const service = storeService(
            this.injector,
            store,
            'ssmAuthInfo',
            ShopeeOpenServiceV2,
        );
        let detailFailures = 0;
        return from$(
            splitWindows(since, moment(), SHOPEE_LIST_WINDOW_DAYS),
        ).pipe(
            mergeMap(
                (window) => service.getReturnList(window.from, window.to),
                1,
            ),
            mergeMap((listRows: any[]) => from$(listRows)),
            mergeMap((listRow: any) => {
                // An in-transit refund comes back on the order's own label,
                // whatever the refund's status: that label's tracking is
                // where its progress is.
                if (withDetails && isShopeeInTransitRefund(listRow)) {
                    return this.shopeeForward(
                        service,
                        _toString(listRow.order_sn),
                    ).pipe(
                        map((forward) =>
                            normalizeShopeeReturn(
                                store,
                                listRow,
                                undefined,
                                forward,
                            ),
                        ),
                        catchError((error: unknown) => {
                            detailFailures++;
                            console.warn(
                                `${store.storeId} return ${listRow.return_sn}: forward tracking failed —`,
                                error,
                            );
                            return of(normalizeShopeeReturn(store, listRow));
                        }),
                    );
                }
                // Nothing ships back on a refund-only return, and a closed
                // case cannot change stage; neither is worth a call.
                if (
                    !withDetails ||
                    listRow.needs_logistics === false ||
                    SHOPEE_FINAL_STATUSES.includes(String(listRow.status))
                ) {
                    return of(normalizeShopeeReturn(store, listRow));
                }
                return service.getReturnDetail(listRow.return_sn).pipe(
                    map((detail) =>
                        normalizeShopeeReturn(store, listRow, detail),
                    ),
                    catchError((error: unknown) => {
                        detailFailures++;
                        console.warn(
                            `${store.storeId} return ${listRow.return_sn}: detail failed —`,
                            error,
                        );
                        return of(normalizeShopeeReturn(store, listRow));
                    }),
                );
            }, SHOPEE_DETAIL_CONCURRENCY),
            toArray(),
            map((rows) => ({ rows, detailFailures })),
        );
    }

    private fetchTiktok(
        store: TiktokStoreConfig,
        since: Moment,
    ): Observable<{ rows: ReturnPackage[] }> {
        const service = storeService(
            this.injector,
            store,
            'tiktokAuthInfo',
            TiktokOpenService,
        );
        return service.searchReturns(since.unix()).pipe(
            map((rows) => ({
                rows: rows.map((row) => normalizeTiktokReturn(store, row)),
            })),
        );
    }

    // ---- Failed deliveries ("ตีกลับ") ----
    //
    // None of the platforms files these as a return, so they come from the
    // order endpoints — see return-package.ts for how each is recognised.

    private fetchFailedDeliveries(
        store: StoreConfig,
        since: Moment,
    ): Observable<PartialFetch> {
        switch (store.platform) {
            case 'lazada':
                return this.fetchLazadaFailedDeliveries(store, since);
            case 'shopee':
                return this.fetchShopeeFailedDeliveries(store, since);
            case 'tiktok':
                return this.fetchTiktokFailedDeliveries(store, since);
        }
    }

    // Orders in failed / shipped_back created in a window that starts early
    // enough to catch a late failure, then their items: the item statuses
    // say which units are coming back and whether they have arrived.
    private fetchLazadaFailedDeliveries(
        store: LazadaStoreConfig,
        since: Moment,
    ): Observable<PartialFetch> {
        const service = storeService(
            this.injector,
            store,
            'lazadaAuthInfo',
            LazadaOpenService,
        );
        const createdAfter = since
            .clone()
            .subtract(LAZADA_ORDER_TO_FAILURE_DAYS, 'days');
        return forkJoin([
            service.getAllOrdersByStatus(
                LazadaOrderStatus.Failed,
                createdAfter,
            ),
            service.getAllOrdersByStatus(
                LazadaOrderStatus.ShippedBack,
                createdAfter,
            ),
        ]).pipe(
            mergeMap(([failed, travelling]) => {
                const orders = _uniqBy(failed.concat(travelling), (order) =>
                    _toString(order.order_id),
                );
                if (orders.length === 0) {
                    return of([] as ReturnPackage[]);
                }
                return service
                    .getOrderItems(
                        orders.map((order) => _toString(order.order_id)),
                    )
                    .pipe(
                        map((itemLists: any[]) =>
                            _compact(
                                orders.map((order) =>
                                    normalizeLazadaFailedDelivery(
                                        store,
                                        order,
                                        _get(
                                            itemLists.find(
                                                (list: any) =>
                                                    _toString(list.order_id) ===
                                                    _toString(order.order_id),
                                            ),
                                            'order_items',
                                        ) || [],
                                    ),
                                ),
                            ),
                        ),
                    );
            }),
            map((rows) => ({
                rows: rows.filter((row) => this.openedSince(row, since)),
            })),
        );
    }

    // Shopee cancels a failed delivery when the courier turns it around,
    // days after the order was placed, so the cancelled orders are listed by
    // UPDATE time; their details sort failed deliveries from cancels before
    // handover, and each failed delivery's label and tracking history say
    // whether it is back yet.
    private fetchShopeeFailedDeliveries(
        store: ShopeeStoreConfig,
        since: Moment,
    ): Observable<PartialFetch> {
        const service = storeService(
            this.injector,
            store,
            'ssmAuthInfo',
            ShopeeOpenServiceV2,
        );
        let detailFailures = 0;
        return from$(
            splitWindows(since, moment(), SHOPEE_LIST_WINDOW_DAYS),
        ).pipe(
            mergeMap(
                (window) =>
                    service.getOrderSnsUpdatedBetween(
                        window.from,
                        window.to,
                        ShopeeOrderStatus.Cacelled,
                    ),
                1,
            ),
            reduce(
                (all: string[], page: any[]) =>
                    all.concat(page.map((entry) => _toString(entry.orderSn))),
                [] as string[],
            ),
            mergeMap((orderSns) =>
                orderSns.length === 0
                    ? of([] as any[])
                    : service
                          .getOrderDetails(_uniq(orderSns))
                          .pipe(
                              reduce(
                                  (all: any[], chunk: any[]) =>
                                      all.concat(chunk),
                                  [] as any[],
                              ),
                          ),
            ),
            mergeMap((orders: any[]) =>
                from$(orders.filter(isShopeeFailedDelivery)),
            ),
            mergeMap((order: any) => {
                const orderSn = _toString(order.order_sn);
                const carrier = _toString(order.shipping_carrier ?? '');
                return this.shopeeForward(service, orderSn, carrier).pipe(
                    catchError((error: unknown) => {
                        detailFailures++;
                        console.warn(
                            `${store.storeId} order ${orderSn}: forward tracking failed —`,
                            error,
                        );
                        return of({
                            trackingNumber: '',
                            carrier,
                            events: [],
                        } as ShopeeForwardTracking);
                    }),
                    map((forward) =>
                        normalizeShopeeFailedDelivery(store, order, forward),
                    ),
                );
            }, SHOPEE_TRACKING_CONCURRENCY),
            toArray(),
            map((rows) => ({ rows, detailFailures })),
        );
    }

    // TikTok cancels a failed delivery when it is back at the shop, 4–11
    // days after the order; the search filters on creation, so it starts
    // early and the rows are cut to the lookback by the cancel time.
    private fetchTiktokFailedDeliveries(
        store: TiktokStoreConfig,
        since: Moment,
    ): Observable<PartialFetch> {
        const service = storeService(
            this.injector,
            store,
            'tiktokAuthInfo',
            TiktokOpenService,
        );
        return service
            .searchOrders(
                TiktokOrderStatus.Cancelled,
                since
                    .clone()
                    .subtract(TIKTOK_ORDER_TO_FAILURE_DAYS, 'days')
                    .unix(),
            )
            .pipe(
                map((orders: any[]) => ({
                    rows: orders
                        .filter(isTiktokFailedDelivery)
                        .map((order) =>
                            normalizeTiktokFailedDelivery(store, order),
                        )
                        .filter((row) => this.openedSince(row, since)),
                })),
            );
    }

    // An order's forward label, its carrier and its tracking history — for
    // a parcel coming back on the label it went out on. `carrier` is passed
    // when the order detail is already in hand; otherwise it is fetched,
    // since SPX and Flash labels cannot be told apart by prefix.
    private shopeeForward(
        service: ShopeeOpenServiceV2,
        orderSn: string,
        carrier?: string,
    ): Observable<ShopeeForwardTracking> {
        const carrierName: Observable<string> =
            carrier !== undefined
                ? of(carrier)
                : service.getOrderDetails([orderSn]).pipe(
                      reduce(
                          (all: any[], chunk: any[]) => all.concat(chunk),
                          [] as any[],
                      ),
                      map((orders) =>
                          _toString(orders[0]?.shipping_carrier ?? ''),
                      ),
                  );
        return forkJoin([
            service.getTrackingNumbers([orderSn]),
            service.getTrackingInfo(orderSn),
            carrierName,
        ]).pipe(
            map(([numbers, events, name]) => ({
                trackingNumber: _toString(numbers[0]?.trackingNumber ?? ''),
                carrier: name,
                events,
            })),
        );
    }

    // A return the platform did not date is kept rather than dropped: better
    // an undated row in the table than a package nobody is expecting.
    private openedSince(row: ReturnPackage, since: Moment): boolean {
        return row.createdAt === null || !row.createdAt.isBefore(since);
    }
}
